/**
 * Live phase financial correction layer.
 *
 * The Phases table's Fee/Hours Expended are Airtable ROLLUPS over the legacy "Time Entry Staging"
 * table (see docs/TIME_RECORDS.md, docs/MIGRATION_DERIVATIONS.md). Time entries migrated to Supabase
 * (public.time_entries, services/timecards.js) and nothing writes back to that Airtable table
 * anymore, so those rollups are frozen as of the migration cutover — confirmed 2026-08-06: a phase
 * showing "2.6 hrs expended" had 41.1 Posted hours in Supabase.
 *
 * This module recomputes the expended figures from live Supabase data and layers them over whatever
 * a caller already fetched from Airtable, so every consumer (Time Review, Time Card, Phase Landscape,
 * the outline engine's phase rows) shows the same, correct numbers without duplicating the Airtable
 * fetch itself.
 *
 * Scope decision (Matt, 2026-08): Fee/Hours Expended are Posted-only — mirrors the original rollup's
 * effective behavior and the field's intent (what's actually billed/committed via Core), not
 * provisional pending-review time. `Most Recent Time Entry` (stall detection — "is anyone still
 * touching this phase") is deliberately ALL statuses instead — a different question, and Posted-only
 * would make an actively-worked phase whose time hasn't cleared review yet look falsely stalled.
 */

async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }

/** { [phaseAirtableId]: { hours, fee, recentHours, pendingHours, mostRecentDate, firstPostedDate,
 *  lastPostedDate } } — hours/fee/recentHours/firstPostedDate/lastPostedDate are Posted-only;
 *  recentHours is the trailing RECENT_WINDOW_DAYS window (see api/time-entries.js), feeding the
 *  Fixed-Fee budget-runway projection below; firstPostedDate/lastPostedDate feed that same
 *  projection's minimum-history floor. pendingHours is every non-Rejected, non-Posted hour
 *  (Pending Review/Submitted/Approved/Post Failed) — real logged time not yet reflected in the
 *  Posted-only Hours Expended below, surfaced as a caveat, never mixed into the financial basis
 *  itself. mostRecentDate (YYYY-MM-DD or null) is across every status. */
export async function getPhaseExpendedTotals(phaseIds) {
  const ids = (phaseIds || []).filter(Boolean)
  if (!ids.length) return {}
  const token = await authToken()
  const res = await fetch('/api/time-entries?action=phaseTotals&phaseIds=' + encodeURIComponent(ids.join(',')), {
    headers: token ? { Authorization: 'Bearer ' + token } : {},
  })
  if (!res.ok) { console.error('[phaseFinancials] totals fetch failed: ' + res.status); return {} }
  const data = await res.json().catch(() => ({}))
  return data.totals || {}
}

const numOr = (v, d) => { const n = Number(v); return isFinite(n) ? n : d }

// Trailing window the Fixed-Fee budget-runway projection burns against — must match
// RECENT_WINDOW_DAYS in api/time-entries.js.
const RECENT_WINDOW_DAYS = 14

// Minimum Posted-history span (first Posted entry to last, in days) before the budget-runway
// projection is shown at all. A phase with only a couple days of Posted history (a brand-new
// phase, or one where a big batch just cleared review) has nothing to extrapolate FROM — the
// projection would just be "however much landed in that one batch" stretched across the whole
// remaining budget, which reads as a confident calendar date but is really noise (Matt, 2026-08-11
// — Luisi Residence's Design Development BIM Coordination phase projected ~177 days of runway off
// a single 4-hour Posted batch, all within the trailing window).
const MIN_POSTED_SPAN_DAYS = 5

/**
 * Pure recompute — mirrors the Airtable formula chain exactly, substituting the live Supabase sum for
 * the frozen "…Calculated" rollup. billedFee drives Hours Budget (computed by the caller, unaffected);
 * contractedFee drives Fee Remaining / Fee Performance Percent / Phase Fee Status — the two fee fields
 * feed different Airtable formulas and this preserves that split.
 *
 * effectiveBillingRate — see docs/EBR_FRAMEWORK.md. The white paper defines EBR as fee ÷ hours, a
 * LEADING indicator; billedFee ÷ hours is a lagging, realized-to-date variant (it only moves when
 * Core actually invoices, which happens on the Billing Run's cadence, not daily). Per contract type:
 *   - Fixed Fee, WITH a real contracted amount: contractedFee ÷ hoursExpended. The fee is known at
 *     signing and never scales with hours, so once hours exceed what the fee supports, the rate
 *     dilutes on its own — no cap logic needed, the ceiling IS the formula.
 *   - Hourly, OR any phase with no contracted fee set (regardless of what `Contract Type` says —
 *     it's inconsistently populated; e.g. Permitting/Meetings/Construction-Support-style phases
 *     routinely have it blank rather than explicitly "Hourly", confirmed 2026-08-07 against Osborne
 *     Pismo Beach ADU's Permitting Phase): feeExpended ÷ hoursExpended — the value of every logged
 *     hour at each person's actual billing rate, live, no billing-cycle wait. Treating "no fee to
 *     dilute against" as Hourly-style (rather than defaulting blank Contract Type to Fixed Fee) is
 *     what avoids a nonsense "over a $0 budget" reading — see the isFixedFee line below. AJ doesn't
 *     run capped/not-to-exceed Hourly phases in practice, so there's no ceiling concept to apply
 *     even for a real Fixed-Fee phase whose amount hasn't been entered yet (Matt, 2026-08).
 *
 * projectedBudgetDate — Fixed-Fee only: at the recent burn rate (recentHours over
 * RECENT_WINDOW_DAYS), the calendar date hours are projected to cross the budget. Deliberately NOT
 * computed for Hourly — there's no fixed target to converge toward (the rate moves with staffing
 * mix of new entries, not a burn-to-a-ceiling relationship), and on a small/fast-moving phase a
 * "reaches target by Wednesday" claim would be built on noisy, low-hour-count data and could
 * whipsaw day to day — actively misleading rather than helpful (Matt, 2026-08). Also suppressed
 * entirely until the phase's Posted history spans at least MIN_POSTED_SPAN_DAYS (see above) — the
 * same noise problem applies even harder to a phase that's only just started posting.
 *
 * pendingHours/pendingFee — passed straight through, not used in feeExpended/feeRemaining/
 * phaseFeeStatus/effectiveBillingRate above (those stay Posted-only, per the scope decision at the
 * top of this file). Real hours+fee already logged (Pending Review/Submitted/Approved/Post Failed)
 * that haven't posted yet, so callers can show "X of Y hrs (+ Z pending)" and, via
 * feeExpendedInclPending, "what this phase would read if that pending time posts as-is" — a second
 * angle worth showing alongside the Posted-only figure, not a replacement for it, since rejection is
 * rare in practice and pending time is close to a floor on what's coming (Matt, 2026-08-21).
 */
export function computeLiveFinancials({
  billedFee = 0, contractedFee = 0, hoursBudget = null,
  feeStartingPoint = 0, hoursStartingPoint = 0,
  supabaseFee = 0, supabaseHours = 0,
  contractType = null, recentHours = null, pendingHours = 0, pendingFee = 0,
  firstPostedDate = null, lastPostedDate = null,
} = {}) {
  const feeExpended = numOr(feeStartingPoint, 0) + numOr(supabaseFee, 0)
  const feeExpendedInclPending = feeExpended + numOr(pendingFee, 0)
  const hoursExpended = numOr(hoursStartingPoint, 0) + numOr(supabaseHours, 0)
  const hb = Number(hoursBudget)
  const hoursRemaining = hoursBudget != null && isFinite(hb) ? hb - hoursExpended : null
  const cf = numOr(contractedFee, 0)
  const feeRemaining = !cf ? 0 : (feeExpended >= cf ? 0 : cf - feeExpended)
  const percentFeeRemaining = cf ? feeRemaining / cf : 0
  const feePerformancePercent = cf > 0 ? feeExpended / cf : 1
  const phaseFeeStatus = !cf ? 'No Budget Set'
    : feeRemaining === 0 ? '⚠️ Over Budget'
    : (feeRemaining / cf) <= 0.1 ? '🔴 At Risk'
    : (feeRemaining / cf) <= 0.25 ? '🟡 Watch'
    : '🟢 On Track'

  const isFixedFee = contractType !== 'Hourly' && cf > 0
  const effectiveBillingRate = hoursExpended > 0
    ? (isFixedFee ? (cf > 0 ? cf / hoursExpended : null) : (feeExpended / hoursExpended))
    : null

  // Posted-history floor: don't project off a phase that's barely started posting (see
  // MIN_POSTED_SPAN_DAYS above).
  const postedSpanDays = (firstPostedDate && lastPostedDate)
    ? Math.round((new Date(lastPostedDate + 'T00:00:00').getTime() - new Date(firstPostedDate + 'T00:00:00').getTime()) / 86400000)
    : null
  const hasSufficientPostedHistory = postedSpanDays != null && postedSpanDays >= MIN_POSTED_SPAN_DAYS

  let projectedBudgetDate = null
  if (isFixedFee && hoursRemaining != null && hoursRemaining > 0 && recentHours != null && hasSufficientPostedHistory) {
    const avgDailyBurn = numOr(recentHours, 0) / RECENT_WINDOW_DAYS
    if (avgDailyBurn > 0) {
      const daysToBudget = hoursRemaining / avgDailyBurn
      const d = new Date(); d.setDate(d.getDate() + Math.round(daysToBudget))
      projectedBudgetDate = d.toISOString().slice(0, 10)
    }
  }

  return {
    feeExpended, hoursExpended, hoursRemaining, feeRemaining, percentFeeRemaining, feePerformancePercent,
    phaseFeeStatus, effectiveBillingRate, projectedBudgetDate,
    pendingHours: numOr(pendingHours, 0), pendingFee: numOr(pendingFee, 0), feeExpendedInclPending,
  }
}

/**
 * Take raw Airtable phase records ({id, fields}) and return NEW records with the Expended-family
 * fields corrected from live Supabase totals. Non-mutating — safe to call on cached arrays. Falls
 * back to the original records (with their stale Airtable values) if the totals fetch fails, so a
 * transient network error degrades to "old behavior" rather than blanking the numbers.
 */
export async function enrichPhaseRecords(records) {
  const list = records || []
  const ids = list.map(r => r.id).filter(Boolean)
  if (!ids.length) return list
  let totals = {}
  try { totals = await getPhaseExpendedTotals(ids) }
  catch (e) { console.error('[phaseFinancials] enrichPhaseRecords failed', e && e.message); return list }
  return list.map(r => {
    const f = r.fields || {}
    const t = totals[r.id] || { hours: 0, fee: 0, recentHours: 0, pendingHours: 0, pendingFee: 0, mostRecentDate: null, firstPostedDate: null, lastPostedDate: null }
    const c = computeLiveFinancials({
      billedFee: numOr(f['Billed Fee'], 0),
      contractedFee: numOr(f['Contracted Fee'], 0),
      hoursBudget: f['Hours Budget'],
      feeStartingPoint: numOr(f['Fee Expended Starting Point'], 0),
      hoursStartingPoint: numOr(f['Hours Expended Starting Point'], 0),
      supabaseFee: t.fee, supabaseHours: t.hours,
      contractType: f['Contract Type'], recentHours: t.recentHours, pendingHours: t.pendingHours, pendingFee: t.pendingFee,
      firstPostedDate: t.firstPostedDate, lastPostedDate: t.lastPostedDate,
    })
    return {
      ...r,
      fields: {
        ...f,
        'Fee Expended': c.feeExpended,
        'Hours Expended': c.hoursExpended,
        'Hours Remaining': c.hoursRemaining,
        'Fee Remaining': c.feeRemaining,
        'Percent Fee Remaining': c.percentFeeRemaining,
        'Fee Performance Percent': c.feePerformancePercent,
        'Phase Fee Status': c.phaseFeeStatus,
        'Effective Billing Rate': c.effectiveBillingRate,
        'Projected Budget Date': c.projectedBudgetDate,
        'Pending Hours': c.pendingHours,
        'Pending Fee': c.pendingFee,
        'Fee Expended Incl Pending': c.feeExpendedInclPending,
        'Most Recent Time Entry': t.mostRecentDate || null,
      },
    }
  })
}
